import { useAuthState } from '../../services/auth';
import { useEffect, useState } from 'react';
import './general-list.scss';
import { TableWidget } from '../../components/table-widget/TableWidget';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useOperatorsState } from '../../services/OperatorsList';
import axios from 'axios';
import {
  useCPEs_Managed_Card_Store,
  useThreats_mitigated_cardStore,
  useDevices_managed,
  useMobile_with_app_card_Store,
} from '../../stores/store';
import Pagination from 'rc-pagination';
import CountUp from 'react-countup';
import {
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  Bug,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Cpu,
  Download,
  Filter,
  Globe,
  Hash,
  Inbox,
  LayoutGrid,
  List,
  LucideIcon,
  MonitorSmartphone,
  Network,
  RotateCcw,
  Router,
  Search,
  ShieldAlert,
  ShieldCheck,
  Smartphone,
  UserX,
  Wifi,
  X,
} from 'lucide-react';
import { getLanguage } from '../../util/languages';
import '../../design-system/index.css';
import { Trans, useTranslation } from 'react-i18next';
import { LoadingSpinner } from '../../components/loading-spinner/LoadingSpinner';

// Define TypeScript interfaces for the data
interface CpeDevice {
  ID: number;
  "CPE ID": number;
  Registrant: string;
  "Alternative Registrant": string;
  "Date Registered": string;
  "Date Verified": string;
  "Firmware Version": string;
  "Serial Number": string;
  "Last Boot Time": string;
  "Last Seen Time": string;
  "Risk Score": string;
}

interface NetworkDevice {
  ID: number;
  "CPE ID": number;
  Name: string;
  "Device Type": string;
  "MAC Address": string;
  Confirmed: string;
  Description: string;
  "Internet Allowed": string;
  "Connection Status": string;
  "Risk Score": string;
}

interface CpeApiResponse {
  headers: Array<{
    key: string;
    sortable: boolean;
  }>;
  data: CpeDevice[];
  records: number;
  size: number;
  start: number;
  end: number;
  page: number;
  pages: number;
}

interface DeviceApiResponse {
  headers: Array<{
    key: string;
    sortable: boolean;
  }>;
  data: NetworkDevice[];
  records: number;
  size: number;
  start: number;
  end: number;
  page: number;
  pages: number;
}

// Filter options interfaces
interface AccountFilterOptions {
  status: string;
  riskScore: string;
  firmwareVersion: string;
}

interface DeviceFilterOptions {
  deviceType: string;
  connectionStatus: string;
  internetAllowed: string;
  riskScore: string;
  confirmedStatus: string;
}

// The API sends 'N/A' / 'Invalid Date' for accounts that were never verified
const isVerifiedDate = (value?: string | null) => !!value && value !== 'N/A' && value !== 'Invalid Date';

export const GeneralList = (props: any) => {
  const { t, i18n } = useTranslation();
  const { operatorId, title } = useParams();
  const { operatorList, loggedUserData } = useOperatorsState();
  const { token } = useAuthState();
  const [tableApiUrl, setTableApiUrl] = useState<any>();
  const [defaultDate, setDefaultDate] = useState<any>();
  const location = useLocation();
  const navigate = useNavigate();
  const [customerId, setCustomerId] = useState('');
  const [userEmail, setUserEmail] = useState<string | null>();
  const [accountPage, setAccountPage] = useState<boolean>(false);
  const [devicePage, setDevicePage] = useState<boolean>(false);
  const [accountsData, setAccountsData] = useCPEs_Managed_Card_Store((state) => [state.data, state.setData]);
  const [devicesManaged, setDevicesManaged] = useDevices_managed((state) => [state.data, state.setData]);
  const [unverified, setUnverified] = useState<boolean>(false);
  const [noData, setNoData] = useState<boolean>(false);

  // Card layout state
  const [cpeData, setCpeData] = useState<CpeDevice[]>([]);
  const [deviceData, setDeviceData] = useState<NetworkDevice[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [displayData, setDisplayData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [useCardView, setUseCardView] = useState<boolean>(true);

  // Filter states
  const [accountFilters, setAccountFilters] = useState<AccountFilterOptions>({
    status: 'all',
    riskScore: 'all',
    firmwareVersion: 'all'
  });

  const [deviceFilters, setDeviceFilters] = useState<DeviceFilterOptions>({
    deviceType: 'all',
    connectionStatus: 'all',
    internetAllowed: 'all',
    riskScore: 'all',
    confirmedStatus: 'all'
  });

  // Enhanced loading state
  const [loadingState, setLoadingState] = useState<{
    currentSize: number;
    totalRecords: number;
    allDataFetched: boolean;
    isLoadingMore: boolean;
  }>({
    currentSize: 500,
    totalRecords: 0,
    allDataFetched: false,
    isLoadingMore: false
  });

  const [pagination, setPagination] = useState({
    page: 1,
    size: 9, // 9 cards per page
    total: 0,
  });

  const [appInstalledData, setAppInstalledData] = useState<any>();
  const [deviceCount, setDeviceCount] = useState<any>();
  const [threatData, setThreatData] = useState<any>();
  const [dnsRequests, setDnsRequests] = useState<any>();
  const [unverifedAcc, setUnverifedAcc] = useState<any>();
  const [cveCount, setCveCount] = useState<any>();

// Get unique values for dropdown options (excluding N/A and Unknown)
const getUniqueFirmwareVersions = () => {
  const versions = cpeData
    .map(cpe => cpe["Firmware Version"])
    .filter(version => version && version !== 'N/A' && version !== 'Unknown');
  return ['all', ...Array.from(new Set(versions))];
};
// Helper function to check if any filters are applied
const isAnyFilterApplied = () => {
  if (accountPage) {
    return (
      accountFilters.status !== 'all' ||
      accountFilters.riskScore !== 'all' ||
      accountFilters.firmwareVersion !== 'all' ||
      searchQuery.trim() !== ''
    );
  } else if (devicePage) {
    return (
      deviceFilters.deviceType !== 'all' ||
      deviceFilters.connectionStatus !== 'all' ||
      deviceFilters.internetAllowed !== 'all' ||
      deviceFilters.riskScore !== 'all' ||
      deviceFilters.confirmedStatus !== 'all' ||
      searchQuery.trim() !== ''
    );
  }
  return false;
};

// Helper function to count active filters
const getActiveFiltersCount = () => {
  let count = 0;
  
  if (searchQuery.trim() !== '') count++;
  
  if (accountPage) {
    if (accountFilters.status !== 'all') count++;
    if (accountFilters.riskScore !== 'all') count++;
    if (accountFilters.firmwareVersion !== 'all') count++;
  } else if (devicePage) {
    if (deviceFilters.deviceType !== 'all') count++;
    if (deviceFilters.connectionStatus !== 'all') count++;
    if (deviceFilters.internetAllowed !== 'all') count++;
    if (deviceFilters.riskScore !== 'all') count++;
    if (deviceFilters.confirmedStatus !== 'all') count++;
  }
  
  return count;
};

// Enhanced resetFilters function
const resetFilters = () => {
  if (accountPage) {
    setAccountFilters({
      status: 'all',
      riskScore: 'all',
      firmwareVersion: 'all'
    });
  } else if (devicePage) {
    setDeviceFilters({
      deviceType: 'all',
      connectionStatus: 'all',
      internetAllowed: 'all',
      riskScore: 'all',
      confirmedStatus: 'all'
    });
  }
  setSearchQuery('');
  
  // Optional: Show a brief confirmation
  setTimeout(() => {
    // You could add a toast notification here if desired
    console.log('All filters have been reset');
  }, 100);
};
const getUniqueDeviceTypes = () => {
  const types = deviceData
    .map(device => device["Device Type"])
    .filter(type => type && type !== 'N/A' && type !== 'Unknown');
  return ['all', ...Array.from(new Set(types))];
};

  const fetchWidgetCounts = async () => {
    try {
      const response = await axios.get(`/v2/op/master-operator/${operatorId}/global-counts`);
      setDnsRequests(response.data.data[0].past24HourDns ? response.data.data[0].past24HourDns : 0);
      setDeviceCount(response.data.data[0].devicesCount ? response.data.data[0].devicesCount : 0);
      setThreatData(response.data.data[0].past24HourThreats ? response.data.data[0].past24HourThreats : 0);
      setAppInstalledData(response.data.data[0].appInstalled ? response.data.data[0].appInstalled : 0);
      setUnverifedAcc(response.data.data[0].unverifiedAccounts ? response.data.data[0].unverifiedAccounts : 0);
      setCveCount(response.data.data[0].cveCount ? response.data.data[0].cveCount : 0);
    } catch (error) {
      console.error('Error fetching widget data:', error);
    }
  };

  const handleUnverifiedAccounts = () => {
    setUnverified(true);
    navigate(`/general-list/${operatorId}/operatorcpe/Total Unverified Accounts Managed`);
  };

  useEffect(() => {
    fetchWidgetCounts();
  }, [operatorId]);

  // THEME HANDLER
  const useTheme = () => {
    const [darkMode, setDarkMode] = useState<boolean>(() => {
      const savedMode = localStorage.getItem('darkMode');
      if (savedMode) return JSON.parse(savedMode);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    });

    useEffect(() => {
      const root = document.documentElement;
      root.classList.toggle('dark-theme', darkMode);
      root.classList.toggle('light-theme', !darkMode);
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        metaThemeColor.setAttribute('content', darkMode ? '#121212' : '#ffffff');
      }
    }, [darkMode]);

    const toggleTheme = () => {
      setDarkMode((prevMode) => {
        const newMode = !prevMode;
        localStorage.setItem('darkMode', JSON.stringify(newMode));
        setTimeout(() => window.location.reload(), 350);
        return newMode;
      });
    };

    return { darkMode, toggleTheme };
  };

  const { darkMode, toggleTheme } = useTheme();

  const getEmailFromLocalStorage = (): string | null => {
    const email = localStorage.getItem('email');
    setUserEmail(email);
    return email || null;
  };

  useEffect(() => {
    const email = getEmailFromLocalStorage();
    if (email) setUserEmail(email);
  }, []);

  // Enhanced data loading with progressive size increase
  const fetchDataWithProgressiveLoading = async (isInitialLoad = false) => {
    if (!operatorId) return;
    
    if (isInitialLoad) {
      setIsLoading(true);
      setLoadingState(prev => ({ ...prev, currentSize: 500, allDataFetched: false }));
    } else {
      setLoadingState(prev => ({ ...prev, isLoadingMore: true }));
    }

    try {
      let url = '';
      let response;

      if (accountPage) {
        if (!unverified) {
          url = `v2/op/account/${operatorId}/list-accounts`;
        } else {
          url = `v2/op/account/${operatorId}/list-accounts?unverified=true`;
        }
        
        const separator = url.includes('?') ? '&' : '?';
        url += `${separator}size=${loadingState.currentSize}`;

  
        response = await axios.get<CpeApiResponse>(url);
        
        const result = response.data;
        const cpeDevices: CpeDevice[] = result.data || [];
        
        if (isInitialLoad) {
          setCpeData(cpeDevices);
          setFilteredData(cpeDevices);
        } else {
          const mergedData = [...cpeData, ...cpeDevices];
          setCpeData(mergedData);
          setFilteredData(mergedData);
        }

        const totalRecords = result.records || cpeDevices.length;
        const currentFetchedCount = isInitialLoad ? cpeDevices.length : cpeData.length + cpeDevices.length;
        
        setLoadingState(prev => ({
          ...prev,
          totalRecords,
          allDataFetched: currentFetchedCount >= totalRecords || cpeDevices.length < loadingState.currentSize
        }));

        if (cpeDevices.length === loadingState.currentSize && currentFetchedCount < totalRecords) {
          setLoadingState(prev => ({ 
            ...prev, 
            currentSize: prev.currentSize + 500 
          }));
        }

        setNoData(cpeDevices.length === 0);

      } else if (devicePage) {
        url = `v2/op/device/${operatorId}/list-devices`;
        const separator = url.includes('?') ? '&' : '?';
        url += `${separator}size=${loadingState.currentSize}`;
        response = await axios.get<DeviceApiResponse>(url);
        
        const result = response.data;
        const devices: NetworkDevice[] = result.data || [];
        
        if (isInitialLoad) {
          setDeviceData(devices);
          setFilteredData(devices);
        } else {
          const mergedData = [...deviceData, ...devices];
          setDeviceData(mergedData);
          setFilteredData(mergedData);
        }

        const totalRecords = result.records || devices.length;
        const currentFetchedCount = isInitialLoad ? devices.length : deviceData.length + devices.length;
        
        setLoadingState(prev => ({
          ...prev,
          totalRecords,
          allDataFetched: currentFetchedCount >= totalRecords || devices.length < loadingState.currentSize
        }));

        if (devices.length === loadingState.currentSize && currentFetchedCount < totalRecords) {
          setLoadingState(prev => ({ 
            ...prev, 
            currentSize: prev.currentSize + 500 
          }));
        }

        setNoData(devices.length === 0);
      }

    } catch (error: any) {
      console.error(`Error fetching ${accountPage ? 'CPE' : 'device'} data:`, error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      setNoData(true);
      
      if (isInitialLoad) {
        setCpeData([]);
        setDeviceData([]);
        setFilteredData([]);
      }
    } finally {
      if (isInitialLoad) {
        setIsLoading(false);
      } else {
        setLoadingState(prev => ({ ...prev, isLoadingMore: false }));
      }
    }
  };

  // Initial data load
  const fetchInitialData = async () => {
    await fetchDataWithProgressiveLoading(true);
  };

  // Load more data
  const loadMoreData = async () => {
    if (!loadingState.allDataFetched && !loadingState.isLoadingMore) {
      await fetchDataWithProgressiveLoading(false);
    }
  };

// Apply filters to data
// Update the applyFilters function to handle N/A and Unknown values properly
const applyFilters = () => {
  let filtered = accountPage ? cpeData : deviceData;

  // Apply search filter
  if (searchQuery.trim() !== '') {
    const query = searchQuery.toLowerCase();
    if (accountPage) {
      filtered = (filtered as CpeDevice[]).filter(
        (cpe) =>
          cpe.Registrant?.toLowerCase().includes(query) ||
          cpe["Alternative Registrant"]?.toLowerCase().includes(query) ||
          cpe["Serial Number"]?.toLowerCase().includes(query) ||
          cpe["Firmware Version"]?.toLowerCase().includes(query) ||
          cpe.ID?.toString().includes(query) ||
          cpe["CPE ID"]?.toString().includes(query)
      );
    } else {
      filtered = (filtered as NetworkDevice[]).filter(
        (device) =>
          device.Name?.toLowerCase().includes(query) ||
          device["Device Type"]?.toLowerCase().includes(query) ||
          device["MAC Address"]?.toLowerCase().includes(query) ||
          device.Description?.toLowerCase().includes(query) ||
          device.ID?.toString().includes(query) ||
          device["CPE ID"]?.toString().includes(query)
      );
    }
  }

  // Apply account-specific filters
  if (accountPage) {
    filtered = (filtered as CpeDevice[]).filter(cpe => {
      // Status filter (Verified/Unverified)
      if (accountFilters.status !== 'all') {
        const isVerified = isVerifiedDate(cpe["Date Verified"]);
        if (accountFilters.status === 'verified' && !isVerified) return false;
        if (accountFilters.status === 'unverified' && isVerified) return false;
      }

      // Risk score filter - exclude N/A and Unknown
      if (accountFilters.riskScore !== 'all') {
        const riskScore = cpe["Risk Score"];
        const hasValidRiskScore = riskScore && riskScore !== 'N/A' && riskScore !== 'Unknown';
        
        if (accountFilters.riskScore === 'none' && hasValidRiskScore) return false;
        if (accountFilters.riskScore !== 'none' && hasValidRiskScore) {
          if (accountFilters.riskScore.toLowerCase() !== riskScore.toLowerCase()) return false;
        }
        // If filter is for specific risk score but item has no valid risk score, exclude it
        if (accountFilters.riskScore !== 'none' && !hasValidRiskScore) return false;
      }

      // Firmware version filter - exclude N/A
      if (accountFilters.firmwareVersion !== 'all') {
        const firmwareVersion = cpe["Firmware Version"];
        const hasValidFirmware = firmwareVersion && firmwareVersion !== 'N/A';
        
        if (accountFilters.firmwareVersion === 'none' && hasValidFirmware) return false;
        if (accountFilters.firmwareVersion !== 'none' && hasValidFirmware) {
          if (accountFilters.firmwareVersion !== firmwareVersion) return false;
        }
        // If filter is for specific firmware but item has no valid firmware, exclude it
        if (accountFilters.firmwareVersion !== 'none' && !hasValidFirmware) return false;
      }

      return true;
    });

    // 🔄 SORT ACCOUNTS: Unverified accounts first, then verified accounts
    filtered = (filtered as CpeDevice[]).sort((a, b) => {
      // Properly handle N/A and Invalid Date values
      const aIsVerified = isVerifiedDate(a["Date Verified"]);
      const bIsVerified = isVerifiedDate(b["Date Verified"]);
      
      if (!aIsVerified && bIsVerified) return -1; // a comes first
      if (aIsVerified && !bIsVerified) return 1;  // b comes first
      return 0; // maintain order for same verification status
    });
  }

  // Apply device-specific filters
  if (devicePage) {
    filtered = (filtered as NetworkDevice[]).filter(device => {
      // Device type filter - exclude N/A and Unknown
      if (deviceFilters.deviceType !== 'all') {
        const deviceType = device["Device Type"];
        const hasValidDeviceType = deviceType && deviceType !== 'N/A' && deviceType !== 'Unknown';
        
        if (deviceFilters.deviceType === 'none' && hasValidDeviceType) return false;
        if (deviceFilters.deviceType !== 'none' && hasValidDeviceType) {
          if (deviceFilters.deviceType !== deviceType) return false;
        }
        // If filter is for specific device type but item has no valid type, exclude it
        if (deviceFilters.deviceType !== 'none' && !hasValidDeviceType) return false;
      }

      // Connection status filter - exclude N/A and Unknown
      if (deviceFilters.connectionStatus !== 'all') {
        const connectionStatus = device["Connection Status"];
        const hasValidConnection = connectionStatus && connectionStatus !== 'N/A' && connectionStatus !== 'Unknown';
        
        if (deviceFilters.connectionStatus === 'none' && hasValidConnection) return false;
        if (deviceFilters.connectionStatus !== 'none' && hasValidConnection) {
          const normalizedStatus = connectionStatus.toLowerCase().replace('_', ' ');
          const normalizedFilter = deviceFilters.connectionStatus.toLowerCase();
          if (normalizedFilter !== normalizedStatus) return false;
        }
        // If filter is for specific connection but item has no valid connection status, exclude it
        if (deviceFilters.connectionStatus !== 'none' && !hasValidConnection) return false;
      }

      // Internet allowed filter - exclude N/A and Unknown
      if (deviceFilters.internetAllowed !== 'all') {
        const internetAllowed = device["Internet Allowed"];
        const hasValidInternet = internetAllowed && internetAllowed !== 'N/A' && internetAllowed !== 'Unknown';
        
        if (deviceFilters.internetAllowed === 'none' && hasValidInternet) return false;
        if (deviceFilters.internetAllowed !== 'none' && hasValidInternet) {
          const normalizedAllowed = internetAllowed.toLowerCase();
          const normalizedFilter = deviceFilters.internetAllowed.toLowerCase();
          if (normalizedFilter !== normalizedAllowed) return false;
        }
        // If filter is for specific internet status but item has no valid status, exclude it
        if (deviceFilters.internetAllowed !== 'none' && !hasValidInternet) return false;
      }

      // Risk score filter - exclude N/A and Unknown
      if (deviceFilters.riskScore !== 'all') {
        const riskScore = device["Risk Score"];
        const hasValidRiskScore = riskScore && riskScore !== 'N/A' && riskScore !== 'Unknown';
        
        if (deviceFilters.riskScore === 'none' && hasValidRiskScore) return false;
        if (deviceFilters.riskScore !== 'none' && hasValidRiskScore) {
          if (deviceFilters.riskScore.toLowerCase() !== riskScore.toLowerCase()) return false;
        }
        // If filter is for specific risk score but item has no valid risk score, exclude it
        if (deviceFilters.riskScore !== 'none' && !hasValidRiskScore) return false;
      }

      // Confirmed status filter - exclude N/A and Unknown
      if (deviceFilters.confirmedStatus !== 'all') {
        const confirmedStatus = device.Confirmed;
        const hasValidConfirmed = confirmedStatus && confirmedStatus !== 'N/A' && confirmedStatus !== 'Unknown';
        
        if (deviceFilters.confirmedStatus === 'none' && hasValidConfirmed) return false;
        if (deviceFilters.confirmedStatus !== 'none' && hasValidConfirmed) {
          if (deviceFilters.confirmedStatus.toLowerCase() !== confirmedStatus.toLowerCase()) return false;
        }
        // If filter is for specific confirmed status but item has no valid status, exclude it
        if (deviceFilters.confirmedStatus !== 'none' && !hasValidConfirmed) return false;
      }

      return true;
    });
  }

  setFilteredData(filtered);
  setPagination(prev => ({
    ...prev,
    page: 1,
    total: filtered.length
  }));
};


  useEffect(() => {
    applyFilters();
  }, [searchQuery, cpeData, deviceData, accountFilters, deviceFilters, accountPage]);

  // Paginate filtered data - Show 9 cards per page
  useEffect(() => {
    const startIndex = (pagination.page - 1) * pagination.size;
    const endIndex = startIndex + pagination.size;
    const paginatedResults = filteredData.slice(startIndex, endIndex);
    setDisplayData(paginatedResults);
  }, [filteredData, pagination.page, pagination.size]);

  const handlePageChange = (page: number, pageSize?: number) => {
    setPagination(prev => ({
      ...prev,
      page,
      size: pageSize || prev.size
    }));
  };

  // MAIN API CALL - SIMPLIFIED VERSION
  async function fetchApi() {
    try {

      if (location.pathname.includes('operatorcpe')) {

        setAccountPage(true);
        setDevicePage(false);
        
        if (useCardView) {
          await fetchInitialData();
        } else {
          let url = `v2/op/account/${operatorId}/list-accounts`;
          if (unverified) {
            url += '?unverified=true';
          }
          const accounts = await axios.get(url);
          setTableApiUrl(url);
          setAccountsData(accounts.data);
          setNoData(!accounts.data.data || accounts.data.data.length === 0);
        }
      } else if (location.pathname.includes('operatordevices')) {
        setAccountPage(false);
        setDevicePage(true);
        
        if (useCardView) {
          await fetchInitialData();
        } else {
          const url = `v2/op/device/${operatorId}/list-devices`;
          const devices = await axios.get(url);
          setTableApiUrl(url);
          setAccountsData(devices.data);
          setNoData(!devices.data.data || devices.data.data.length === 0);
        }
      }
    } catch (error: any) {
      console.error('Error in fetchApi:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      setNoData(true);
    }
  }

  useEffect(() => {
    if (token && operatorId) {
      fetchApi();
    }
  }, [token, location.pathname, unverified, useCardView]);

  // Reset when operatorId changes
  useEffect(() => {
    if (operatorId) {
      setCpeData([]);
      setDeviceData([]);
      setFilteredData([]);
      setLoadingState({
        currentSize: 500,
        totalRecords: 0,
        allDataFetched: false,
        isLoadingMore: false
      });
      resetFilters();
    }
  }, [operatorId]);

  // Force reload when switching to card view
  useEffect(() => {
    if (useCardView && token && operatorId) {
      if (accountPage || devicePage) {
        fetchInitialData();
      }
    }
  }, [useCardView, accountPage, devicePage]);

  async function tokenCheck() {
    const token = localStorage.getItem('loginToken');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCustomerId(payload.operatorId);
      } catch (error) {
        console.error('Error parsing token:', error);
      }
    }
  }

  useEffect(() => {
    tokenCheck();
  }, []);

  function onTableClick(rowData: any) {
    const cpeID = rowData.key['CPE ID'];
    if (location.pathname.includes('operatorcpe')) {
      navigate(`/operators/${operatorId || rowData.key.operator_id}/cpe/${cpeID}`);
    } else if (location.pathname.includes('operatordevices')) {
      navigate(`/operators/${operatorId || rowData.key.operator_id}/cpe/${cpeID}/d/${rowData.key.ID}/devicesPage`);
    }
  }

  function onCardClick(item: any) {
    const cpeID = item["CPE ID"];
    if (location.pathname.includes('operatorcpe')) {
      navigate(`/operators/${operatorId}/cpe/${cpeID}`);
    } else if (location.pathname.includes('operatordevices')) {
      navigate(`/operators/${operatorId}/cpe/${cpeID}/d/${item.ID}/devicesPage`);
    }
  }

  // 📅 Improved date formatter
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return t('list.values.notAvailable');

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return t('list.values.invalidDate');

    return date.toLocaleString(getLanguage(i18n.language).locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Design-system chip tones
  const getRiskTone = (riskScore: string): string => {
    switch (riskScore?.toLowerCase()) {
      case 'high': return 'danger';
      case 'medium': return 'warn';
      case 'low': return 'mint';
      default: return '';
    }
  };

  const getStatusText = (dateVerified: string | null | undefined): string => {
    if (!isVerifiedDate(dateVerified)) return t('list.status.pending');
    return t('list.status.verified');
  };

  // Display labels for server values used in logic/tone mapping; unknown values are shown as-is
  const valueLabelKeys: { [key: string]: string } = {
    'high': 'list.values.high',
    'medium': 'list.values.medium',
    'low': 'list.values.low',
    'connected': 'list.values.connected',
    'not_connected': 'list.values.notConnected',
    'not connected': 'list.values.notConnected',
    'allowed': 'list.values.allowed',
    'not allowed': 'list.values.notAllowed',
    'confirmed': 'list.values.confirmed',
    'unconfirmed': 'list.values.unconfirmed',
    'unknown': 'list.values.unknown',
    'n/a': 'list.values.notAvailable',
  };
  const valueLabel = (value: string | null | undefined, fallbackKey = 'list.values.unknown'): string => {
    if (!value) return t(fallbackKey);
    const key = valueLabelKeys[value.toLowerCase().trim()];
    return key ? t(key) : value;
  };

  const getConnectionTone = (status: string): string => {
    switch (status?.toLowerCase()) {
      case 'connected': return 'mint';
      case 'not_connected': return 'danger';
      default: return '';
    }
  };

  const getInternetTone = (allowed: string): string => {
    switch (allowed?.toLowerCase()) {
      case 'allowed': return 'mint';
      case 'not allowed': return 'danger';
      default: return '';
    }
  };

  type FilterOption = { value: string; label: string; count: number };

  // Labelled native select with counts in the option text
  const renderFilterSelect = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    options: FilterOption[],
    meta?: React.ReactNode
  ) => (
    <div className="gl-filter">
      <label className="bd-field-label">{label}</label>
      <div className={`bd-select ${value !== 'all' ? 'active' : ''}`}>
        <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {`${option.label} (${option.count.toLocaleString()})`}
            </option>
          ))}
        </select>
        <ChevronDown size={14} />
      </div>
      {meta && <div className="gl-filter-meta">{meta}</div>}
    </div>
  );

  const countBy = <T,>(rows: T[], predicate: (row: T) => boolean) => rows.filter(predicate).length;
  const isEmptyValue = (value: string) => !value || value === 'N/A' || value === 'Unknown';

  const renderAccountFilters = () => {
    const verified = countBy(cpeData, (cpe) => isVerifiedDate(cpe['Date Verified']));
    const unverifiedCount = cpeData.length - verified;
    const risk = (level: string) => countBy(cpeData, (cpe) => cpe['Risk Score']?.toLowerCase() === level);
    const versions = getUniqueFirmwareVersions().filter((v) => v !== 'all');

    return (
      <>
        {renderFilterSelect(
          t('list.filters.verificationStatus'),
          accountFilters.status,
          (status) => setAccountFilters((prev) => ({ ...prev, status })),
          [
            { value: 'all', label: t('list.filters.allStatus'), count: cpeData.length },
            { value: 'verified', label: t('list.filters.verified'), count: verified },
            { value: 'unverified', label: t('list.filters.unverified'), count: unverifiedCount },
          ],
          <>
            <span className="bd-chip mint">{t('list.filters.chips.verified', { value: verified })}</span>
            <span className="bd-chip warn">{t('list.filters.chips.unverified', { value: unverifiedCount })}</span>
          </>
        )}
        {renderFilterSelect(
          t('list.filters.riskLevel'),
          accountFilters.riskScore,
          (riskScore) => setAccountFilters((prev) => ({ ...prev, riskScore })),
          [
            { value: 'all', label: t('list.filters.allRiskScores'), count: cpeData.length },
            { value: 'high', label: t('list.filters.highRisk'), count: risk('high') },
            { value: 'medium', label: t('list.filters.mediumRisk'), count: risk('medium') },
            { value: 'low', label: t('list.filters.lowRisk'), count: risk('low') },
            { value: 'none', label: t('list.filters.noRiskScore'), count: countBy(cpeData, (cpe) => isEmptyValue(cpe['Risk Score'])) },
          ],
          <>
            <span className="bd-chip danger">{t('list.filters.chips.high', { value: risk('high') })}</span>
            <span className="bd-chip warn">{t('list.filters.chips.medium', { value: risk('medium') })}</span>
            <span className="bd-chip mint">{t('list.filters.chips.low', { value: risk('low') })}</span>
          </>
        )}
        {renderFilterSelect(
          t('list.filters.firmwareVersion'),
          accountFilters.firmwareVersion,
          (firmwareVersion) => setAccountFilters((prev) => ({ ...prev, firmwareVersion })),
          [
            { value: 'all', label: t('list.filters.allVersions'), count: cpeData.length },
            {
              value: 'none',
              label: t('list.filters.noFirmwareVersion'),
              count: countBy(cpeData, (cpe) => !cpe['Firmware Version'] || cpe['Firmware Version'] === 'N/A'),
            },
            ...versions.map((version) => ({
              value: version,
              label: version,
              count: countBy(cpeData, (cpe) => cpe['Firmware Version'] === version),
            })),
          ],
          <span className="bd-meta">
            {t('list.filters.uniqueVersions')} <span className="bd-mono">{versions.length}</span>
          </span>
        )}
      </>
    );
  };

  const renderDeviceFilters = () => {
    const types = getUniqueDeviceTypes().filter((t) => t !== 'all');
    const connection = (status: string) =>
      countBy(deviceData, (d) => d['Connection Status']?.toLowerCase().replace('_', ' ') === status);
    const internet = (value: string) => countBy(deviceData, (d) => d['Internet Allowed']?.toLowerCase() === value);
    const risk = (level: string) => countBy(deviceData, (d) => d['Risk Score']?.toLowerCase() === level);
    const confirmed = (value: string) => countBy(deviceData, (d) => d.Confirmed?.toLowerCase() === value);

    return (
      <>
        {renderFilterSelect(
          t('list.filters.deviceType'),
          deviceFilters.deviceType,
          (deviceType) => setDeviceFilters((prev) => ({ ...prev, deviceType })),
          [
            { value: 'all', label: t('list.filters.allDeviceTypes'), count: deviceData.length },
            { value: 'none', label: t('list.filters.noDeviceType'), count: countBy(deviceData, (d) => isEmptyValue(d['Device Type'])) },
            ...types.map((type) => ({
              value: type,
              label: type,
              count: countBy(deviceData, (d) => d['Device Type'] === type),
            })),
          ],
          <span className="bd-meta">
            {t('list.filters.types')} <span className="bd-mono">{types.length}</span>
          </span>
        )}
        {renderFilterSelect(
          t('list.filters.connection'),
          deviceFilters.connectionStatus,
          (connectionStatus) => setDeviceFilters((prev) => ({ ...prev, connectionStatus })),
          [
            { value: 'all', label: t('list.filters.allConnections'), count: deviceData.length },
            { value: 'connected', label: t('list.filters.connected'), count: connection('connected') },
            { value: 'not connected', label: t('list.filters.notConnected'), count: connection('not connected') },
            { value: 'none', label: t('list.filters.noStatus'), count: countBy(deviceData, (d) => isEmptyValue(d['Connection Status'])) },
          ],
          <>
            <span className="bd-chip mint">{t('list.filters.chips.online', { value: connection('connected') })}</span>
            <span className="bd-chip danger">{t('list.filters.chips.offline', { value: connection('not connected') })}</span>
          </>
        )}
        {renderFilterSelect(
          t('list.filters.internetAccess'),
          deviceFilters.internetAllowed,
          (internetAllowed) => setDeviceFilters((prev) => ({ ...prev, internetAllowed })),
          [
            { value: 'all', label: t('list.filters.allInternet'), count: deviceData.length },
            { value: 'allowed', label: t('list.filters.allowed'), count: internet('allowed') },
            { value: 'not allowed', label: t('list.filters.notAllowed'), count: internet('not allowed') },
            { value: 'none', label: t('list.filters.noStatus'), count: countBy(deviceData, (d) => isEmptyValue(d['Internet Allowed'])) },
          ],
          <>
            <span className="bd-chip mint">{t('list.filters.chips.allowed', { value: internet('allowed') })}</span>
            <span className="bd-chip danger">{t('list.filters.chips.blocked', { value: internet('not allowed') })}</span>
          </>
        )}
        {renderFilterSelect(
          t('list.filters.riskLevel'),
          deviceFilters.riskScore,
          (riskScore) => setDeviceFilters((prev) => ({ ...prev, riskScore })),
          [
            { value: 'all', label: t('list.filters.allRiskScores'), count: deviceData.length },
            { value: 'high', label: t('list.filters.highRisk'), count: risk('high') },
            { value: 'medium', label: t('list.filters.mediumRisk'), count: risk('medium') },
            { value: 'low', label: t('list.filters.lowRisk'), count: risk('low') },
            { value: 'none', label: t('list.filters.noRiskScore'), count: countBy(deviceData, (d) => isEmptyValue(d['Risk Score'])) },
          ],
          <>
            <span className="bd-chip danger">{t('list.filters.chips.high', { value: risk('high') })}</span>
            <span className="bd-chip warn">{t('list.filters.chips.mediumShort', { value: risk('medium') })}</span>
            <span className="bd-chip mint">{t('list.filters.chips.low', { value: risk('low') })}</span>
          </>
        )}
        {renderFilterSelect(
          t('list.filters.confirmation'),
          deviceFilters.confirmedStatus,
          (confirmedStatus) => setDeviceFilters((prev) => ({ ...prev, confirmedStatus })),
          [
            { value: 'all', label: t('list.filters.allStatus'), count: deviceData.length },
            { value: 'confirmed', label: t('list.filters.confirmed'), count: confirmed('confirmed') },
            { value: 'unconfirmed', label: t('list.filters.unconfirmed'), count: confirmed('unconfirmed') },
            { value: 'none', label: t('list.filters.noStatus'), count: countBy(deviceData, (d) => isEmptyValue(d.Confirmed)) },
          ],
          <>
            <span className="bd-chip mint">{t('list.filters.chips.confirmed', { value: confirmed('confirmed') })}</span>
            <span className="bd-chip warn">{t('list.filters.chips.pending', { value: confirmed('unconfirmed') })}</span>
          </>
        )}
      </>
    );
  };

  const cardKeyDown = (item: any) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') onCardClick(item);
  };

  // Render CPE Card
  const renderCpeCard = (cpe: CpeDevice) => {
    const verifiedAccount = isVerifiedDate(cpe['Date Verified']);
    return (
      <div
        key={cpe.ID}
        className="bd-item-card gl-account-card"
        role="button"
        tabIndex={0}
        onClick={() => onCardClick(cpe)}
        onKeyDown={cardKeyDown(cpe)}
      >
        <div className="bd-item-card-hd">
          <span className="bd-avatar sm">{(cpe.Registrant || '?').charAt(0).toUpperCase()}</span>
          <div className="bd-item-card-id">
            <h3 className="bd-item-card-name bd-truncate" title={cpe.Registrant}>{cpe.Registrant || t('list.values.notAvailable')}</h3>
            {cpe['Alternative Registrant'] && cpe['Alternative Registrant'] !== cpe.Registrant && (
              <span className="bd-meta bd-truncate" title={cpe['Alternative Registrant']}>
                {t('list.card.alt', { name: cpe['Alternative Registrant'] })}
              </span>
            )}
          </div>
          <span className={`bd-chip ${verifiedAccount ? 'mint' : 'warn'}`}>
            <span className="dot" />
            {getStatusText(cpe['Date Verified'])}
          </span>
        </div>

        <div className="bd-item-card-rows">
          <div className="bd-item-card-row">
            <span className="bd-item-card-label"><Hash size={12} /> {t('list.card.ids')}</span>
            <span className="bd-meta bd-mono">{t('list.card.cpeThenDeviceIds', { cpeId: cpe['CPE ID'], deviceId: cpe.ID })}</span>
          </div>
          <div className="bd-item-card-row">
            <span className="bd-item-card-label"><Router size={12} /> {t('list.card.serial')}</span>
            <span className="bd-tag bd-truncate" title={cpe['Serial Number']}>{cpe['Serial Number'] || t('list.values.notAvailable')}</span>
          </div>
          <div className="bd-item-card-row">
            <span className="bd-item-card-label"><Cpu size={12} /> {t('list.card.firmware')}</span>
            <span className="bd-chip bd-mono bd-truncate" title={cpe['Firmware Version']}>{cpe['Firmware Version'] || t('list.values.notAvailable')}</span>
          </div>
          <div className="bd-item-card-row">
            <span className="bd-item-card-label"><ShieldAlert size={12} /> {t('list.card.risk')}</span>
            <span className={`bd-chip ${getRiskTone(cpe['Risk Score'])}`}>{valueLabel(cpe['Risk Score'])}</span>
          </div>
        </div>

        <div className="bd-item-card-dates">
          <div>
            <span className="bd-meta">{t('list.card.lastBoot')}</span>
            <span className="bd-item-card-date">{formatDate(cpe['Last Boot Time'])}</span>
          </div>
          <div>
            <span className="bd-meta">{t('list.card.lastSeen')}</span>
            <span className="bd-item-card-date">{formatDate(cpe['Last Seen Time'])}</span>
          </div>
          <div>
            <span className="bd-meta">{t('list.card.registered')}</span>
            <span className="bd-item-card-date">{formatDate(cpe['Date Registered'])}</span>
          </div>
          <div>
            <span className="bd-meta">{t('list.card.verified')}</span>
            <span className="bd-item-card-date">{formatDate(cpe['Date Verified'])}</span>
          </div>
        </div>

        <div className="bd-item-card-ft">
          {t('list.card.viewAccountDetails')}
          <ArrowRight size={13} />
        </div>
      </div>
    );
  };

  // SVG mapping for device types
  const deviceTypeIcons: { [key: string]: string } = {
    'smartphone': '/icons/Smartphone.svg',
    'laptop': '/icons/Laptop.svg',
    'desktop': '/icons/DesktopComputer.svg',
    'games console': '/icons/GamesConsole.svg',
    'wearable': '/icons/Wearable.svg',
    'tablet': '/icons/Tablet.svg',
    'printer': '/icons/Printer.svg',
    'smart tv': '/icons/SmartTV.svg',
    'smart speaker': '/icons/SmartSpeaker.svg',
    'home security device': '/icons/HomeSecurity.svg',
    'wifi extender': '/icons/WiFiExtenders.svg',
    'unknown': '/icons/WarningTriangle.svg',
    'laptop computer': '/icons/Laptop.svg',
    'desktop computer': '/icons/DesktopComputer.svg',
    'smart devices': '/icons/SmartHomeDevices.svg',
    'smart home device': '/icons/SmartHomeDevices.svg',
    'other devices': '/icons/OtherDevices.svg',
    'other connected device': '/icons/OtherDevices.svg',
    'home security': '/icons/HomeSecurity.svg',
  };

  // Helper function to get icon for device type
  const getDeviceIcon = (deviceType: string): string => {
    const normalizedType = deviceType?.toLowerCase().trim() || 'unknown';
    return deviceTypeIcons[normalizedType] || '';
  };

  // Render Device Card with SVG Icons
  const renderDeviceCard = (device: NetworkDevice) => {
    const deviceIcon = getDeviceIcon(device['Device Type']);
    const isConfirmed = device.Confirmed === 'Confirmed';

    return (
      <div
        key={device.ID}
        className="bd-item-card"
        role="button"
        tabIndex={0}
        onClick={() => onCardClick(device)}
        onKeyDown={cardKeyDown(device)}
      >
        <div className="bd-item-card-hd">
          <span className="gl-device-icon">
            {deviceIcon ? (
              <img
                src={deviceIcon}
                alt=""
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <MonitorSmartphone size={18} />
            )}
          </span>
          <div className="bd-item-card-id">
            <h3 className="bd-item-card-name bd-truncate" title={device.Name}>{device.Name || t('list.values.notAvailable')}</h3>
            <span className="bd-meta bd-truncate" title={device.Description}>{device.Description || t('list.card.noDescription')}</span>
          </div>
          <span className="bd-chip bd-truncate gl-type-chip" title={device['Device Type']}>
            {device['Device Type'] || t('list.values.unknown')}
          </span>
        </div>

        <div className="bd-item-card-rows">
          <div className="bd-item-card-row">
            <span className="bd-item-card-label"><Hash size={14} /> {t('list.card.ids')}</span>
            <span className="bd-meta bd-mono">{t('list.card.deviceThenCpeIds', { deviceId: device.ID, cpeId: device['CPE ID'] })}</span>
          </div>
          <div className="bd-item-card-row">
            <span className="bd-item-card-label"><Network size={14} /> MAC</span>
            <span className="bd-tag bd-truncate" title={device['MAC Address']}>{device['MAC Address'] || t('list.values.notAvailable')}</span>
          </div>
          <div className="bd-item-card-row">
            <span className="bd-item-card-label"><Wifi size={14} /> {t('list.card.connection')}</span>
            <span className={`bd-chip ${getConnectionTone(device['Connection Status'])}`}>
              <span className="dot" />
              {valueLabel(device['Connection Status']?.replace('_', ' '))}
            </span>
          </div>
          <div className="bd-item-card-row">
            <span className="bd-item-card-label"><Globe size={14} /> {t('list.card.internet')}</span>
            <span className={`bd-chip ${getInternetTone(device['Internet Allowed'])}`}>
              {valueLabel(device['Internet Allowed'])}
            </span>
          </div>
          <div className="bd-item-card-row">
            <span className="bd-item-card-label"><ShieldAlert size={14} /> {t('list.card.risk')}</span>
            <span className={`bd-chip ${getRiskTone(device['Risk Score'])}`}>{valueLabel(device['Risk Score'])}</span>
          </div>
          <div className="bd-item-card-row gl-row-divider">
            <span className="bd-item-card-label"><BadgeCheck size={14} /> {t('list.card.confirmed')}</span>
            <span className={`bd-chip ${isConfirmed ? 'mint' : 'warn'}`}>{valueLabel(device.Confirmed)}</span>
          </div>
        </div>

        <div className="bd-item-card-ft">
          {t('list.card.viewDeviceDetails')}
          <ArrowRight size={14} />
        </div>
      </div>
    );
  };

  // Get load more button text and state
  const getLoadMoreButtonState = () => {
    if (loadingState.allDataFetched) {
      return { text: t('list.toolbar.allDataFetched'), disabled: true, show: true };
    } else if (loadingState.isLoadingMore) {
      return { text: t('list.toolbar.loadingMoreData'), disabled: true, show: true };
    }
    return { text: t('list.toolbar.loadMoreData'), disabled: false, show: true };
  };

  const loadMoreButton = getLoadMoreButtonState();
  const theme = (props.darkMode ?? darkMode) ? 'dark' : 'light';
  const itemLabel = accountPage ? 'accounts' : 'devices';
  // Sentences mentioning the item type have their own keys per page, since case/gender differs by language
  const itemKey = `list.items.${itemLabel}`;
  const filtersApplied = isAnyFilterApplied();
  const activeFilterCount = getActiveFiltersCount();

  type Kpi = { title: string; value: any; icon: LucideIcon; linkUrl?: string; linkedpage: string; onClick?: () => void };

  const accountKpis: Kpi[] = [
    {
      title: t('list.kpi.accounts.unverifiedAccounts'),
      value: unverifedAcc,
      icon: UserX,
      linkUrl: `/general-list/${operatorId}/operatorcpe/Total Unverified Accounts Managed`,
      linkedpage: 'Accounts',
      onClick: handleUnverifiedAccounts,
    },
    {
      title: t('list.kpi.accounts.devicesManaged'),
      value: deviceCount,
      icon: MonitorSmartphone,
      linkUrl: `/general-list/${operatorId}/operatordevices/Total Devices Managed`,
      linkedpage: 'Devices',
    },
    { title: t('list.kpi.accounts.dnsRequestsToday'), value: dnsRequests, icon: Globe, linkedpage: 'Activity' },
    {
      title: t('list.kpi.accounts.threatsBlockedToday'),
      value: threatData,
      icon: ShieldCheck,
      linkUrl: `/threatsmanaged/${loggedUserData?.operatorId}`,
      linkedpage: 'Threats',
    },
  ];

  // Links mirror the Accounts page: DNS has no link (Activity can be disabled per operator)
  // and the Angel App card doesn't link back to the page it's on.
  const deviceKpis: Kpi[] = [
    { title: t('list.kpi.devices.dnsRequestsToday'), value: dnsRequests, icon: Globe, linkedpage: 'Activity' },
    { title: t('list.kpi.devices.angelAppInstalled'), value: appInstalledData, icon: Smartphone, linkedpage: 'Devices' },
    {
      title: t('list.kpi.devices.threatsBlockedToday'),
      value: threatData,
      icon: ShieldCheck,
      linkUrl: `/threatsmanaged/${loggedUserData?.operatorId}`,
      linkedpage: 'Threats',
    },
    { title: t('list.kpi.devices.criticalHighCves'), value: cveCount, icon: Bug, linkedpage: 'Threats' },
  ];

  // Same behaviour as WidgetBox: onClick wins, otherwise navigate to linkUrl
  const openKpi = (kpi: Kpi) => {
    if (kpi.onClick) return kpi.onClick();
    if (!kpi.linkUrl) return;
    navigate(kpi.linkUrl, { state: { key: kpi.linkedpage, timestamp: new Date().toISOString() } });
    window.localStorage.setItem('currentNav', kpi.linkedpage);
    if (props.isActive) props.isActive(kpi.linkedpage);
  };

  const renderKpis = (kpis: Kpi[]) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {kpis.map((kpi) => {
        const Icon = kpi.icon;
        const interactive = !!(kpi.onClick || kpi.linkUrl);
        return (
          <div
            key={kpi.title}
            className={`bd-card bd-kpi ${interactive ? 'interactive' : ''}`}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
            onClick={interactive ? () => openKpi(kpi) : undefined}
            onKeyDown={interactive ? (e) => { if (e.key === 'Enter') openKpi(kpi); } : undefined}
          >
            <span className="bd-kpi-icon">
              <Icon size={18} strokeWidth={1.75} />
            </span>
            <div className="bd-kpi-text">
              <span className="bd-kpi-label" title={kpi.title}>{kpi.title}</span>
              <CountUp className="bd-stat-num bd-kpi-num" preserveValue={true} end={Number(kpi.value) || 0} duration={2} separator="," />
            </div>
            {interactive && <ArrowUpRight size={16} className="bd-kpi-arrow" />}
          </div>
        );
      })}
    </div>
  );

  // Display-only translations of the known route titles; unknown titles are shown as-is
  const routeTitleKeys: { [key: string]: string } = {
    'Total Accounts Managed': 'list.routeTitles.totalAccountsManaged',
    'Total Unverified Accounts Managed': 'list.routeTitles.totalUnverifiedAccountsManaged',
    'Total Devices Managed': 'list.routeTitles.totalDevicesManaged',
  };
  const displayTitle = title && routeTitleKeys[title] ? t(routeTitleKeys[title]) : title;

  const pageTitle = title?.includes('Accounts') ? t('list.page.managedAccounts') : title?.includes('Devices') ? t('list.page.managedDevices') : displayTitle;
  const pageSubtitle = title?.includes('Accounts')
    ? t('list.page.accountsSubtitle')
    : title?.includes('Devices')
    ? t('list.page.devicesSubtitle')
    : displayTitle;

  return (
    <div className="bd gl-page" data-theme={theme} data-accent="mint">
      <div className="gl-wrap">
        <header className="gl-hd">
          <span className="gl-eyebrow">{accountPage ? t('list.page.eyebrowAccounts') : devicePage ? t('list.page.eyebrowDevices') : t('list.page.eyebrowOperator')}</span>
          <h1 className="bd-h1 gl-title">{pageTitle}</h1>
          <span className="bd-body">{pageSubtitle}</span>
        </header>

        <section id="general-list">
          {customerId == operatorId ? (
            <div className="gl-content">
              {accountPage && renderKpis(accountKpis)}
              {devicePage && renderKpis(deviceKpis)}

              {/* Toolbar: load status, load more, view toggle */}
              {(accountPage || devicePage) && (
                <div className="gl-toolbar">
                  <span className="bd-meta gl-toolbar-status">
                    {isLoading ? (
                      <>
                        <span className="bd-spinner" /> {t('list.toolbar.loading')}
                      </>
                    ) : (
                      <>
                        <Trans
                          i18nKey={loadingState.totalRecords > 0 ? `${itemKey}.loadedOfTotal` : `${itemKey}.loaded`}
                          count={filteredData.length}
                          values={{
                            loaded: filteredData.length.toLocaleString(),
                            total: loadingState.totalRecords.toLocaleString(),
                          }}
                          components={{ num: <span className="bd-mono" /> }}
                        />
                        {loadingState.allDataFetched && (
                          <span className="bd-chip mint gl-toolbar-chip">
                            <Check size={12} strokeWidth={2.5} /> {t('list.toolbar.allFetched')}
                          </span>
                        )}
                      </>
                    )}
                  </span>
                  <div className="gl-toolbar-actions">
                    {loadMoreButton.show && !loadingState.allDataFetched && (
                      <button
                        type="button"
                        onClick={loadMoreData}
                        disabled={loadMoreButton.disabled}
                        className="bd-pill gl-btn"
                      >
                        {loadingState.isLoadingMore ? <span className="bd-spinner" /> : <Download size={14} />}
                        {loadMoreButton.text}
                      </button>
                    )}
                    <div className="bd-segmented" role="group" aria-label={t('list.toolbar.viewMode')}>
                      <button
                        type="button"
                        className={useCardView ? 'active' : ''}
                        aria-pressed={useCardView}
                        onClick={() => setUseCardView(true)}
                      >
                        <LayoutGrid size={14} /> {t('list.toolbar.cards')}
                      </button>
                      <button
                        type="button"
                        className={!useCardView ? 'active' : ''}
                        aria-pressed={!useCardView}
                        onClick={() => setUseCardView(false)}
                      >
                        <List size={14} /> {t('list.toolbar.table')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* 🧾 CARD VIEW OR TABLE VIEW */}
              {noData ? (
                <div className="bd-card gl-empty">
                  <Inbox size={28} strokeWidth={1.5} />
                  {accountPage ? t('list.empty.noAccounts') : devicePage ? t('list.empty.noDevices') : t('list.empty.noData')}
                </div>
              ) : (accountPage || devicePage) && useCardView ? (
                <div className="bd-card gl-cards">
                  {/* 🔍 Search + filters */}
                  <div className="gl-filters-hd">
                    <label className="bd-search gl-search">
                      <Search size={15} />
                      <input
                        type="text"
                        placeholder={accountPage ? t('list.search.accountsPlaceholder') : t('list.search.devicesPlaceholder')}
                        aria-label={t(`${itemKey}.search`)}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Escape') setSearchQuery(''); }}
                      />
                      {searchQuery && (
                        <button
                          type="button"
                          className="bd-search-clear"
                          aria-label={t('list.search.clear')}
                          onClick={(e) => {
                            setSearchQuery('');
                            e.currentTarget.parentElement?.querySelector('input')?.focus();
                          }}
                        >
                          <X size={12} strokeWidth={2.5} />
                        </button>
                      )}
                    </label>

                    <div className="gl-filters-summary">
                      <span className="bd-meta">
                        <Trans
                          i18nKey={`${itemKey}.showing`}
                          count={filteredData.length}
                          values={{ shown: displayData.length, total: filteredData.length.toLocaleString() }}
                          components={{ num: <span className="bd-mono" /> }}
                        />
                      </span>
                      {filtersApplied && (
                        <>
                          <span className="bd-chip mint">
                            <Filter size={12} />
                            {t('list.filters.activeCount', { count: activeFilterCount })}
                          </span>
                          <button type="button" className="bd-pill ghost gl-btn gl-reset" onClick={resetFilters}>
                            <RotateCcw size={13} />
                            {t('list.filters.reset')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className={`gl-filters ${devicePage ? 'devices' : 'accounts'}`}>
                    {accountPage && renderAccountFilters()}
                    {devicePage && renderDeviceFilters()}
                  </div>

                  {/* Cards Grid - 9 cards per page (3x3 grid) */}
                  <div className="gl-cards-body">
                    {isLoading ? (
                      <LoadingSpinner text={t(`${itemKey}.loading`)} />
                    ) : displayData.length === 0 ? (
                      <div className="gl-cards-empty">
                        {t(`${itemKey}.noMatch`)}
                        {filtersApplied && (
                          <button type="button" className="bd-pill gl-btn" onClick={resetFilters}>
                            <RotateCcw size={13} /> {t('list.empty.resetFilters')}
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {displayData.map((item) =>
                          accountPage ? renderCpeCard(item as CpeDevice) : renderDeviceCard(item as NetworkDevice)
                        )}
                      </div>
                    )}
                  </div>

                  {/* Pagination - Only show if there are more than 9 items */}
                  {filteredData.length > 9 && (
                    <div className="gl-cards-ft">
                      <span className="bd-meta">
                        <Trans
                          i18nKey="list.pagination.pageOf"
                          values={{ page: pagination.page, pages: Math.ceil(filteredData.length / pagination.size) }}
                          components={{ num: <span className="bd-mono" /> }}
                        />
                      </span>
                      <Pagination
                        className="pagination bd-pagination"
                        current={pagination.page}
                        pageSize={pagination.size}
                        total={filteredData.length}
                        onChange={handlePageChange}
                        nextIcon={<ChevronRight size={16} />}
                        prevIcon={<ChevronLeft size={16} />}
                      />
                    </div>
                  )}
                </div>
              ) : (
                /* TABLE VIEW */
                tableApiUrl && (
                  <div className="bd-card gl-table-card">
                    <TableWidget
                      defaultDate={defaultDate}
                      hasSearch={true}
                      key={tableApiUrl}
                      onRowSelect={onTableClick}
                      url={tableApiUrl}
                      countPerPage={9}
                      hideTimes={true}
                      title={displayTitle}
                      subTitle={accountPage ? t('list.table.accountsSubtitle') : t('list.table.devicesSubtitle')}
                    />
                  </div>
                )
              )}
            </div>
          ) : (
            <LoadingSpinner />
          )}
        </section>
      </div>
    </div>
  );
};
